import { memo, type ReactNode } from 'react';
import { type MenuConfig, type MenuItem } from './constants';
import { getDatabaseComponent } from './helpers';
import { Button } from '~/components/ui/Button';
import { useSheetNavigation } from '~/lib/context/sheetNavigation';
import { SettingsPage } from '~/lib/stores/menu';
import { classNames } from '~/utils/classNames';

interface MenuSectionProps {
  config: MenuConfig;
  goBack: () => void;
}

export const DatabaseMenuSection = memo(({ config, goBack }: MenuSectionProps) => {
  const { stack, push, reset } = useSheetNavigation();

  const handleTopLevelNavigation = (title: string, component: ReactNode) => {
    if (stack.length === 1 && stack[0].title === title) {
      return;
    }

    reset();
    push({ title, component });
  };

  const currentTab = stack.length > 0 ? stack[0].title : null;

  return (
    <section className="flex flex-col justify-between h-full">
      <div className="flex flex-col">
        <div
          className={classNames(
            'sticky top-0 bg-bolt-ds-surface z-layer-1 py-1 text-bolt-ds-textTertiary text-xs font-semibold',
          )}
        >
          <Button
            variant="ghost"
            startIcon="i-lucide:chevron-left size-4"
            className="gap-3 w-full shrink-0 justify-center md:justify-start h-10 p-0 md:px-2.5 rounded-md text-sm font-medium bg-transparent hover:bg-bolt-ds-surfaceHighlight text-bolt-ds-textTertiary/60"
            onClick={goBack}
          >
            <span className="hidden md:inline-block text-xs font-semibold">Back to Settings</span>
          </Button>
        </div>
        <ul className="flex flex-col gap-1">
          {config.items.map((item) => (
            <li key={item.id}>
              <DatabaseSettingTab
                menuItem={item}
                currentTab={currentTab}
                onChooseTab={() => {
                  handleTopLevelNavigation(item.text, getDatabaseComponent(item.id));
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
});

interface DatabaseSettingTabProps {
  menuItem: MenuItem;
  currentTab: string | null;
  onChooseTab: (currentPage: SettingsPage) => void;
}

const DatabaseSettingTab = memo(({ menuItem, currentTab, onChooseTab }: DatabaseSettingTabProps) => {
  if (!currentTab) {
    return null;
  }

  const isActive = currentTab === menuItem.text;

  return (
    <button
      key={menuItem.id}
      className={classNames(
        'w-full flex items-center shrink-0 justify-center md:justify-start gap-3 h-[33px] px-2.5 rounded-md text-sm font-medium',
        {
          'bg-bolt-ds-surfaceHighlight text-bolt-ds-textPrimary': isActive,
          'bg-transparent hover:bg-bolt-ds-surfaceHighlight text-bolt-ds-textTertiary/60': !isActive,
        },
      )}
      onClick={() => onChooseTab(menuItem.id)}
    >
      <span
        className={classNames(menuItem.icon, 'size-4 shrink-0', {
          'text-bolt-ds-iconPrimary': isActive,
          'text-bolt-ds-iconSecondary/50': !isActive,
        })}
      />
      <span className="truncate hidden md:inline-block">{menuItem.text}</span>
    </button>
  );
});
